import { useEffect, useState } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { format, parseISO } from "date-fns";
import styles from "./AuctionFilterOptions.module.css";
import "react-datepicker/dist/react-datepicker.css";
import { useMixPanelForTracking } from "../../../shared_utilities/trackMixPanel";

const buildSelectOptions = (values = []) =>
  values.map((val) => ({ label: val, value: val }));

export default function AuctionFilterOptions({
  options = {},
  filters,
  setFilters,
  auctionBucket,
}) {
  const auctionDateList = (options.auction_date || []).map((d) => parseISO(d));
  const {TrackMixPanel} = useMixPanelForTracking()
  const [startDate, setStartDate] = useState(
    filters.start_date ? parseISO(filters.start_date) : null
  );
  const [endDate, setEndDate] = useState(
    filters.end_date ? parseISO(filters.end_date) : null
  );

  const handleChange = (key, selectedOptions) => {
    if (!selectedOptions?.length) {
      const updated = { ...filters };
      delete updated[key];
      setFilters(updated);
    } else {
      setFilters({
        ...filters,
        [key]: selectedOptions.map((opt) => opt.value),
      });
    }
  };

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);

    // Only update filters if both dates are set OR both are cleared
    const hasStart = !!start;
    const hasEnd = !!end;

    const updated = { ...filters };

    if (hasStart && hasEnd) {
      updated.start_date = format(start, "yyyy-MM-dd");
      updated.end_date = format(end, "yyyy-MM-dd");
      setFilters(updated);
    } else if (!hasStart && !hasEnd) {
      delete updated.start_date;
      delete updated.end_date;
      setFilters(updated);
    } else {
      // Don’t set filters at all if only one side is selected
      // Keeps query keys stable
    }
  };

  const clearAll = () => {
    TrackMixPanel({
      Definition: "user has clicked on the clear all button.",
      ModuleName: "tpmp auction",
      ScreenName: `auctions - ${auctionBucket} auctions`,
      Data:{
        "Filters":filters
      }
    });
    setFilters({});
    setStartDate(null);
    setEndDate(null);
  };

  useEffect(() => {
    TrackMixPanel({
      Definition: "user has clicked on the advance filters button.",
      ModuleName: "tpmp auction",
      ScreenName: `auctions - ${auctionBucket} auctions`,
    });
  }, []);

  return (
    <div className={styles.filterWrapper}>
      <div className={styles.filterHeader}>Filter Auctions</div>

      <div className={styles.filterField}>
        <label>Company</label>
        <Select
          isMulti
          options={buildSelectOptions(options.company_name)}
          value={buildSelectOptions(filters.company_name)}
          onChange={(val) => handleChange("company_name", val)}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />
      </div>

      <div className={styles.filterField}>
        <label>Procurement</label>
        <Select
          isMulti
          options={buildSelectOptions(options.procurement_name)}
          value={buildSelectOptions(filters.procurement_name)}
          onChange={(val) => handleChange("procurement_name", val)}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />
      </div>

      <div className={styles.filterField}>
        <label>Auction Description</label>
        <Select
          isMulti
          options={buildSelectOptions(options.auction_description)}
          value={buildSelectOptions(filters.auction_description)}
          onChange={(val) => handleChange("auction_description", val)}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />
      </div>

      <div className={styles.filterField}>
        <div className={styles.dateLabelWrapper}>
          <label>Auction Date Range</label>
          {(startDate || endDate) && (
            <button
              className={styles.resetDateButton}
              onClick={() => {
                setStartDate(null);
                setEndDate(null);
                const updated = { ...filters };
                delete updated.start_date;
                delete updated.end_date;
                setFilters(updated);
              }}
              title="Clear date filter"
            >
              <i className="fa fa-times-circle" aria-hidden="true"></i>
            </button>
          )}
        </div>

        <div className={styles.datePickerWrapper}>
          <DatePicker
            selected={startDate}
            onChange={handleDateRangeChange}
            startDate={startDate}
            endDate={endDate}
            selectsRange
            isClearable
            inline
            highlightDates={[...auctionDateList]}
          />
        </div>
      </div>

      <div className={styles.footer}>
        <button disabled={!filters || !startDate || !endDate} className={styles.clearButton} onClick={clearAll}>
          <i className="fa fa-times mr-1" aria-hidden="true"></i>
          Clear Filters
        </button>
      </div>
    </div>
  );
}
